import i18n from "i18next";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { IconSearch, IconCornerDownLeft } from "@tabler/icons-react";

/**
 * ⌘K command palette — Quiet redesign companion (Pass 3, 2026-04-28).
 *
 * Minimal, no extra dep: portal + scrim + a fuzzy-filtered list. The
 * action registry is passed in from the editor page so we don't have
 * to reach into routes / handlers from inside this component.
 *
 * Keyboard model:
 *   ⌘K / Ctrl+K          — toggle open
 *   Esc                  — close
 *   ↑ / ↓                — move selection
 *   Enter                — invoke selection
 *   tab                  — close (don't trap focus, let Radix handle)
 */

/**
 * Scope tags route actions into the prefix lanes from `Desktop workflows.html`:
 *   `@` → citation   (bib entries)
 *   `§` → section    (headings)
 *   `>` → command    (everything callable; safe default)
 * Untagged actions show in default ("all") mode but disappear when a prefix
 * narrows the lane.
 */
export type CommandScope = "citation" | "section" | "command";

export interface CommandAction {
  id: string;
  label: string;
  /** Optional secondary line ("Switch to dark mode" / "Open the share dialog"). */
  hint?: string;
  /** Section heading: actions group under it ("View", "Actions", "Theme", etc.). */
  group?: string;
  /** Inline icon. */
  icon?: ReactNode;
  /** Keyboard accelerator label, shown on the right ("⌘S", "⌘\"). Display only. */
  shortcut?: string;
  /** Scope lane this action belongs to. Untagged → matched in default mode. */
  scope?: CommandScope;
  run: () => void;
  /** Hide / disable conditionally. */
  disabled?: boolean;
}

/**
 * Detect a scope prefix at the start of the query and return the active scope
 * + the residual query for matching. `>foo` → { scope: "command", q: "foo" }.
 */
function parseScope(raw: string): { scope: CommandScope | null; q: string } {
  const trimmed = raw.trimStart();
  if (trimmed.startsWith("@")) return { scope: "citation", q: trimmed.slice(1) };
  if (trimmed.startsWith("§")) return { scope: "section",  q: trimmed.slice(1) };
  if (trimmed.startsWith(">")) return { scope: "command",  q: trimmed.slice(1) };
  return { scope: null, q: raw };
}

const SCOPE_LABELS: Record<CommandScope, string> = {
  citation: "Citations",
  section:  "Sections",
  command:  "Commands",
};

const SCOPE_PREFIXES: Record<CommandScope, string> = {
  citation: "@",
  section:  "§",
  command:  ">",
};

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actions: CommandAction[];
  /** Deterministic server search over the full document set (⌘K, phase 2c).
   *  Called debounced as the user types; results are appended to the list. */
  documentSearch?: (q: string) => Promise<CommandAction[]>;
}

export function CommandPalette({ open, onOpenChange, actions, documentSearch }: CommandPaletteProps) {
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [remoteDocs, setRemoteDocs] = useState<CommandAction[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const { scope: activeScope, q: residualQuery } = useMemo(() => parseScope(query), [query]);

  const localFiltered = useMemo(() => {
    let visible = actions.filter((a) => !a.disabled);
    // Scope filter: if a prefix is active, restrict to actions of that scope.
    // Untagged actions are excluded from scoped lanes — they show only in
    // default mode (no prefix typed).
    if (activeScope) {
      visible = visible.filter((a) => a.scope === activeScope);
    }
    if (!residualQuery.trim()) return visible;
    const q = residualQuery.toLowerCase();
    return visible
      .map((a) => ({ a, score: scoreMatch(`${a.label} ${a.hint ?? ""}`.toLowerCase(), q) }))
      .filter(({ score }) => score > 0)
      .sort((x, y) => y.score - x.score)
      .map(({ a }) => a);
  }, [actions, activeScope, residualQuery]);

  // Server-side document search (phase 2c) — fires debounced for queries ≥2 chars
  // (no scope prefix); matches are appended below the local results.
  useEffect(() => {
    if (!documentSearch) return;
    const q = residualQuery.trim();
    if (activeScope || q.length < 2) { setRemoteDocs([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try { const res = await documentSearch(q); if (!cancelled) setRemoteDocs(res); }
      catch { if (!cancelled) setRemoteDocs([]); }
    }, 180);
    return () => { cancelled = true; clearTimeout(t); };
  }, [residualQuery, activeScope, documentSearch]);

  // Merge: local actions first, then server docs not already present.
  const filtered = useMemo(() => {
    if (remoteDocs.length === 0) return localFiltered;
    const seen = new Set(localFiltered.map((a) => a.id));
    return [...localFiltered, ...remoteDocs.filter((d) => !seen.has(d.id))];
  }, [localFiltered, remoteDocs]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      const t = setTimeout(() => inputRef.current?.focus(), 10);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (activeIdx >= filtered.length) setActiveIdx(Math.max(0, filtered.length - 1));
  }, [filtered, activeIdx]);

  // Auto-scroll the active row into view.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const node = listRef.current.querySelector<HTMLElement>(`[data-cmd-idx="${activeIdx}"]`);
    node?.scrollIntoView({ block: "nearest" });
  }, [activeIdx, open]);

  // Body-level marker so adjacent UI can dim while the palette is open.
  // Corner overlays read this class and fade themselves to ~30% opacity
  // so the palette owns the visual hierarchy. Cleared in next-render cleanup.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const cls = "lilia-palette-open";
    if (open) document.body.classList.add(cls);
    else document.body.classList.remove(cls);
    return () => document.body.classList.remove(cls);
  }, [open]);

  if (!open) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onOpenChange(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const action = filtered[activeIdx];
      if (action) {
        onOpenChange(false);
        // Defer so the dialog unmounts before the action runs (otherwise
        // a navigation away could re-render the open palette mid-flight).
        setTimeout(() => action.run(), 0);
      }
      return;
    }
  };

  // Group filtered actions by `group` while preserving order.
  const groups: { group: string; items: CommandAction[]; firstIdx: number }[] = [];
  for (let i = 0; i < filtered.length; i++) {
    const a = filtered[i];
    const group = a.group ?? "";
    const last = groups[groups.length - 1];
    if (last && last.group === group) {
      last.items.push(a);
    } else {
      groups.push({ group, items: [a], firstIdx: i });
    }
  }

  const palette = (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center pt-[12vh] px-4"
      role="dialog"
      aria-label={i18n.t("editor:ui.palette")}
      data-testid="command-palette"
      onClick={() => onOpenChange(false)}
    >
      {/* Scrim */}
      <div className="absolute inset-0 bg-black/30 dark:bg-black/50" />
      <div
        className="relative w-full max-w-[560px] rounded-xl bg-[var(--lilia-surface)] border border-[var(--lilia-border)] shadow-[0_24px_64px_rgba(0,0,0,0.18)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="flex items-center gap-2 px-3 h-12 border-b border-[var(--lilia-border)]">
          <IconSearch size={16} className="text-[var(--lilia-text-muted)] shrink-0" />
          {activeScope && (
            <span
              className="font-mono text-[11px] px-1.5 py-0.5 rounded shrink-0"
              style={{
                background: "var(--lilia-primary-subtle)",
                color: "var(--lilia-primary)",
              }}
              title={`Scope: ${SCOPE_LABELS[activeScope]} (${SCOPE_PREFIXES[activeScope]}…)`}
            >
              {SCOPE_LABELS[activeScope]}
            </span>
          )}
          <input
            ref={inputRef}
            data-testid="command-palette-input"
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
            placeholder={
              activeScope === "citation" ? "Search citations…"
              : activeScope === "section" ? "Search sections…"
              : activeScope === "command" ? "Run a command…"
              : "Jump to anything… (try @, §, >)"
            }
            className="flex-1 bg-transparent border-0 outline-none text-[14px] text-[var(--lilia-text)] placeholder:text-[var(--lilia-text-muted)]"
          />
          <span
            className="font-mono text-[10px] text-[var(--lilia-text-muted)] border border-[var(--lilia-border)] rounded px-1.5 py-0.5 shrink-0"
          >{i18n.t("editor:ui.esc")}</span>
        </div>

        <div ref={listRef} className="max-h-[60vh] overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-[var(--lilia-text-muted)]">
              {activeScope
                ? `No ${SCOPE_LABELS[activeScope].toLowerCase()} match.`
                : "No commands match."}
            </div>
          ) : (
            groups.map((g, gi) => (
              <div key={`${g.group}-${gi}`}>
                {g.group && (
                  <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider font-semibold text-[var(--lilia-text-muted)]">
                    {g.group}
                  </div>
                )}
                {g.items.map((a, i) => {
                  const idx = g.firstIdx + i;
                  const active = idx === activeIdx;
                  return (
                    <button
                      key={a.id}
                      data-testid="command-palette-item"
                      data-id={a.id}
                      data-cmd-idx={idx}
                      onMouseEnter={() => setActiveIdx(idx)}
                      onClick={() => {
                        onOpenChange(false);
                        setTimeout(() => a.run(), 0);
                      }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-[13px] ${
                        active
                          ? "bg-[var(--lilia-hover)] text-[var(--lilia-text)]"
                          : "text-[var(--lilia-text)]"
                      }`}
                    >
                      {a.icon && <span className="shrink-0 text-[var(--lilia-text-muted)]">{a.icon}</span>}
                      <span className="flex-1 min-w-0 truncate">{a.label}</span>
                      {a.hint && (
                        <span className="hidden sm:inline text-[11px] text-[var(--lilia-text-muted)] truncate max-w-[160px]">
                          {a.hint}
                        </span>
                      )}
                      {a.shortcut && (
                        <span className="font-mono text-[10px] text-[var(--lilia-text-muted)] border border-[var(--lilia-border)] rounded px-1 py-0.5 shrink-0">
                          {a.shortcut}
                        </span>
                      )}
                      {active && !a.shortcut && (
                        <IconCornerDownLeft size={12} className="text-[var(--lilia-text-muted)] shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="flex items-center gap-3 px-3 py-2 border-t border-[var(--lilia-border)] text-[10px] text-[var(--lilia-text-muted)]">
          <span><kbd className="font-mono px-1 py-0.5 border border-[var(--lilia-border)] rounded">↑</kbd> <kbd className="font-mono px-1 py-0.5 border border-[var(--lilia-border)] rounded">↓</kbd> {i18n.t("editor:ui.navigate")}</span>
          <span><kbd className="font-mono px-1 py-0.5 border border-[var(--lilia-border)] rounded">↵</kbd> {i18n.t("editor:ui.run")}</span>
          {!activeScope && (
            <span className="hidden sm:inline">
              <kbd className="font-mono px-1 py-0.5 border border-[var(--lilia-border)] rounded">@</kbd> cite
              {" "}
              <kbd className="font-mono px-1 py-0.5 border border-[var(--lilia-border)] rounded">§</kbd> section
              {" "}
              <kbd className="font-mono px-1 py-0.5 border border-[var(--lilia-border)] rounded">&gt;</kbd> command
            </span>
          )}
          <div className="flex-1" />
          {/* Per Claude Design round 2 review: "actions" reads as
              dev-jargon — say "commands" instead. */}
          <span>{filtered.length} command{filtered.length === 1 ? "" : "s"}</span>
        </div>
      </div>
    </div>
  );

  return createPortal(palette, document.body);
}

/**
 * Hook to wire a global ⌘K / Ctrl+K listener that toggles the palette open.
 * Call once at the editor shell level.
 */
export function useCommandPaletteShortcut(setOpen: (open: boolean) => void) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isMac = typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
      const accel = isMac ? e.metaKey : e.ctrlKey;
      if (accel && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [setOpen]);
}

// Tiny fuzzy-score: substring boost + sequential-char fallback.
function scoreMatch(haystack: string, needle: string): number {
  if (!needle) return 1;
  if (haystack.includes(needle)) return 100 + (haystack.startsWith(needle) ? 20 : 0);
  let i = 0;
  let score = 0;
  for (const ch of haystack) {
    if (ch === needle[i]) {
      score += 1;
      i += 1;
      if (i === needle.length) return score;
    }
  }
  return 0;
}
